import React from "react";
import { useCarouselWindow } from "./useCarouselWindow";

import {
  Star,
  Globe2,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const brokers = [
  {
    id: "exness",
    name: "Exness",
    country: "Global",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/exness.jpg",
    regulation: "FCA, CySEC",
    minDeposit: "$10",
    platforms: "MT4, MT5",
  },
  {
    id: "fxpro",
    name: "FxPro",
    country: "U.K.",
    rating: 4.9,
    verified: true,
    hero: "/assets/Brokers/fxpro.jpg",
    regulation: "FCA, CySEC",
    minDeposit: "$100",
    platforms: "MT4, MT5, cTrader",
  },
  {
    id: "ibullcapital",
    name: "iBull Capital",
    country: "Mauritius",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/ibullcapital.jpg",
    regulation: "FSC Mauritius",
    minDeposit: "$100",
    platforms: "MT5, MT5 Mobile",
  },

  {
    id: "icmarkets",
    name: "IC Markets",
    country: "Australia",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/icmarkets.jpg",
    regulation: "ASIC, CySEC",
    minDeposit: "$200",
    platforms: "MT4, MT5, cTrader",
  },
  {
    id: "octa",
    name: "Octa",
    country: "Global",
    rating: 4.9,
    verified: true,
    hero: "/assets/Brokers/octa.jpg",
    regulation: "CySEC, SVG",
    minDeposit: "$25",
    platforms: "MT4, MT5",
  },
  {
    id: "pepperstone",
    name: "Pepperstone",
    country: "U.K.",
    rating: 4.7,
    verified: true,
    hero: "/assets/Brokers/pepperstone.jpg",
    regulation: "FCA, ASIC",
    minDeposit: "$200",
    platforms: "MT4, MT5, cTrader",
  },
  {
    id: "tattvam",
    name: "Tattvam",
    country: "UAE / Mauritius",
    rating: 4.7,
    verified: true,
    hero: "/assets/Brokers/tattvam.jpg",
    regulation: "SCA (UAE), FSC (Mauritius)",
    minDeposit: "$1,000+",
    platforms: "MT5, Acuity",
  },
  {
    id: "zerodha",
    name: "Zerodha",
    country: "India",
    rating: 4.8,
    verified: true,
    hero: "/assets/Brokers/zerodha.jpg",
    regulation: "SEBI",
    minDeposit: "₹0",
    platforms: "Kite",
  },
];

export default function FeaturedBrokers() {
  const navigate = useNavigate();
  const VISIBLE = 4;

  const { window, next, prev, pause, resume } = useCarouselWindow({
    total: brokers.length,
    visible: VISIBLE,
    auto: true,
    interval: 3500,
  });

  return (
    <section className="w-full bg-[#0B0F19] py-20">
      <div className="mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white sm:text-3xl lg:text-3xl">
              Featured <span className="text-blue-600">Brokers</span>
            </h2>
            <p className="mt-1 text-sm text-gray-400 sm:text-base">
              Top Rated & Verified Brokers
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={prev}
              className="rounded-full bg-gray-800 p-2 text-white hover:bg-gray-700"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              className="rounded-full bg-gray-800 p-2 text-white hover:bg-gray-700"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="rounded-3xl border border-white/5 bg-[#0B0F19] px-6 py-12 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
          <div
            onMouseEnter={pause}
            onMouseLeave={resume}
            className="flex gap-8 overflow-hidden"
          >
            {window.map((i) => {
              const b = brokers[i];
              return (
                <div
                  key={b.id}
                  onClick={() => navigate(`/brokers/${b.id}`)}
                  className="min-w-[260px] cursor-pointer rounded-3xl border border-white/10 bg-gradient-to-br from-[#12172A] to-[#0B0F19] transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/40"
                >
                  <div className="flex h-44 items-center justify-center rounded-t-3xl bg-white/5 p-4">
                    <img
                      src={b.hero}
                      alt={b.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>

                  <div className="px-5 py-5 text-sm text-gray-400">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white">
                        {b.name}
                      </h3>
                      <div className="flex items-center text-yellow-400">
                        <Star size={16} fill="currentColor" />
                        <span className="ml-1 text-white">{b.rating}</span>
                      </div>
                    </div>

                    {b.verified && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-blue-400">
                        <ShieldCheck size={14} />
                        Verified Broker
                      </div>
                    )}

                    <div className="mt-2 flex items-center gap-2">
                      <Globe2 size={14} />
                      {b.country}
                    </div>

                    <div className="mt-3 space-y-1 text-xs">
                      <div>Regulation: {b.regulation}</div>
                      <div>Min Deposit: {b.minDeposit}</div>
                      <div>Platforms: {b.platforms}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-8 max-w-4xl text-xs text-gray-400">
          Screenshots and visuals are used for review and informational purposes
          only.
        </p>
      </div>
    </section>
  );
}
